import { useEffect, useState } from "react"
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

/**
 * Changing somebody's access, deliberately.
 *
 * The role used to be a live `<Select>` in every row of the users table. Reading
 * down a list of forty people meant forty dropdowns under the cursor, and a
 * stray click — or a scroll wheel over an open one — changed somebody's access
 * in a single request, with no confirmation and nothing on screen afterwards
 * except a different word in a cell.
 *
 * So the table shows the role as text and the change is an explicit action with
 * a dialog behind it. The dialog says the consequence before it happens: saving
 * revokes their sessions, which means whoever it is gets signed out mid-task.
 * That is worth knowing before rather than after.
 */
export default function ChangeRoleDialog({ open, user, roles, onClose, onSave }) {
  const [roleId, setRoleId] = useState("")
  const [busy, setBusy] = useState(false)

  // Reopening on a different person has to start from that person's role, not
  // whatever was left selected last time.
  useEffect(() => {
    if (open) setRoleId(user?.role_id ?? "")
  }, [open, user])

  if (!user) return null

  /* Filtered to the audience the account belongs to. The server refuses a
     cross-audience assignment, so offering one would be offering an option that
     always fails. */
  const eligible = roles.filter((role) =>
    String(user.organization_id) === "0" ? role.audience === "back_office" : role.audience === "client",
  )

  const submit = async (event) => {
    event.preventDefault()
    setBusy(true)
    try {
      await onSave(user.id, roleId === "" ? "" : roleId)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle>Change role</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {user.name || user.username} · {user.organization_label}
            </Typography>

            <TextField
              select
              label="Role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              fullWidth
              autoFocus
              slotProps={{ select: { displayEmpty: true } }}
            >
              <MenuItem value="">No role</MenuItem>
              {eligible.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.label}
                </MenuItem>
              ))}
            </TextField>

            <Typography variant="caption" color="text.secondary">
              Saving signs them out so the change takes effect.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={busy || String(roleId) === String(user.role_id ?? "")}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
